import styled, {
  css,
  FlattenSimpleInterpolation,
  ThemedStyledProps,
} from 'styled-components';
import fonts from '../../helpers/fonts';
import mediaQuery from '../../helpers/mediaQuery';
import spacing from '../../helpers/spacing';
import { Theme } from '../../theme';
import fluidGridColumns from '../../theme/fluidGridColumns';
import { PrimaryCta, SecondaryCta } from '../Cta';
import { Grid, GridItem, GridItemProps } from '../Grid';
import Icon from '../Icon';
import Image from '../Image';
import RichText from '../RichText';

export const NoBackground = styled.div`
  margin: ${spacing(4)} 0;
`;

export const MiniGuideContainer = styled.div`
  padding-top: ${spacing(4)};
  padding-bottom: ${spacing(4)};
  /* Fix for IE width bug */
  width: 100%;
`;

export const SectionsContainer = styled.div`
  ${mediaQuery.tabletLandscape`
    display: flex;
    flex-direction: row;
    /* Enforce arrangement in IE */
    justify-content: space-between;
  `}
`;

const alignmentForThreeSections = (index: number): FlattenSimpleInterpolation => {
  switch (index) {
    case 1:
      return css`
        justify-content: center;
      `;
    case 2:
      return css`
        justify-content: flex-end;
      `;
    default:
      return css`
        justify-content: flex-start;
      `;
  }
};

type SectionWrapperProps = {
  noOfSections: number;
  index: number;
};

export const Section = styled.div<SectionWrapperProps>`
  flex: 1 1 0;
  display: flex;

  margin-top: ${spacing(3)};

  ${mediaQuery.tabletPortrait`
    margin-top: ${spacing(4)};
  `}

  &:first-child {
    margin-top: 0;
  }

  ${mediaQuery.tabletLandscape`
    margin: 0;

    ${({ theme }) => css`
      &:not(:first-child) {
        margin-left: ${theme.layout.tabletLandscape.gutterPx / 2}px;
      }

      &:not(:last-child) {
        margin-right: ${theme.layout.tabletLandscape.gutterPx / 2}px;
      }
    `}

    ${({ noOfSections, index }) => noOfSections === 3 && alignmentForThreeSections(index)}
  `}

  ${mediaQuery.desktop`
    ${({ theme }) => css`
      &:not(:first-child) {
        margin-left: ${theme.layout.desktop.gutterPx / 2}px;
      }

      &:not(:last-child) {
        margin-right: ${theme.layout.desktop.gutterPx / 2}px;
      }
    `}
  `}
`;

type CtasContainerProps = {
  noOfSections: number;
};

export const CtasContainer = styled.div<CtasContainerProps>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: wrap;

  margin-top: ${spacing(1)};
  margin-left: ${spacing(-3)};

  ${mediaQuery.tabletLandscape`
    margin-left: ${spacing(-4)};
  `}

  text-align: left;

  ${mediaQuery.tabletPortrait`
    margin-top: ${spacing(2)};
  `}

  ${mediaQuery.tabletLandscape`
    ${(props) =>
      props.noOfSections > 2 &&
      css`
        justify-content: center;
      `}

    ${(props) =>
      props.noOfSections === 3 &&
      css`
        margin-top: ${spacing(4)};
      `}
  `}
`;

const StyledCta = css`
  flex: 0 1 auto;
  
  margin-left: ${spacing(3)};
  
  ${mediaQuery.tabletLandscape`
    margin-left: ${spacing(4)};
  `}

  margin-top: ${spacing(2)};
  min-width: unset;
`;

export const StyledSecondaryCta = styled(SecondaryCta)`
  ${StyledCta};
`;

export const StyledPrimaryCta = styled(PrimaryCta)`
  ${StyledCta};
`;

export const StyledImage = styled(Image)`
  margin-bottom: ${spacing(3)};

  ${mediaQuery.tabletPortrait`
    margin-bottom: 0;
  `}
`;

const commonHeadingWrapper = css`
  margin: 0 0 ${spacing(3)};

  ${mediaQuery.tabletPortrait`
    margin-bottom: ${spacing(4)};
  `}

  ${mediaQuery.tabletLandscape`
    margin-bottom: ${spacing(6)};
  `}
`;

export const HeadingWrapper = styled.div`
  ${commonHeadingWrapper};

  ${mediaQuery.tabletLandscape`
    text-align: center;
  `}
`;

export const InlineHeadingWrapper = styled.div`
  ${commonHeadingWrapper};

  text-align: left;
`;

export const Heading = styled.h2`
  ${fonts.headingMedium};
  margin: 0;
`;

export const Subheading = styled.p`
  ${fonts.paragraphLarge}
  margin: ${spacing(2)} 0 0;
`;

export const SectionText = styled(RichText)`
  display: inline-block;
  text-align: left;

  & > *:first-child {
    margin-top: ${spacing(0)};
  }

  ${SectionsContainer} & > *:first-child {
    margin-top: 0;
  }

  ul > li {
    margin-top: ${spacing(1)};
    margin-bottom: ${spacing(1)};

    ${mediaQuery.tabletPortrait`
      margin-top: ${spacing(2)};
      margin-bottom: ${spacing(2)};
    `}

    ${mediaQuery.tabletLandscape`
      margin: ${spacing(3)} 0;
    `}

    padding-left: ${(props) => (props.customBulletIcon ? spacing(4) : spacing(2))};
  }

  ${mediaQuery.tabletLandscape`
    ul {
      padding-left: ${(props) => (props.customBulletIcon ? 0 : spacing(1))};
    }
  `}

  h3 {
    margin-bottom: ${spacing(3)};
  }
`;

export const StyledIcon = styled(Icon)`
  margin-right: ${spacing(3)};

  ${mediaQuery.tabletLandscape`
    margin-right: ${spacing(2)};
  `}

  flex: 0 0 auto;
`;

type StyledGridProps = {
  noOfSections: number;
};

export const StyledGrid = styled(Grid)<StyledGridProps>`
  justify-content: center;

  ${(props) => {
    return (
      props.noOfSections > 3 &&
      mediaQuery.tabletLandscape`
        justify-content: space-between;
      `
    );
  }}
`;

export const HeadingGridItem = styled(GridItem)``;

export const ImageGridItem = styled(GridItem)``;

export const SectionGridItem = styled(GridItem)``;

export const CtasGridItem = styled(GridItem)``;

export const FourSectionGridItem = styled(GridItem)`
  &:nth-child(-n + 3) {
    margin-bottom: ${spacing(3)};
  }

  ${mediaQuery.tabletLandscape`
    width: ${(props: ThemedStyledProps<GridItemProps, Theme>) => {
      const itemColumns = props.tabletLandscape ?? fluidGridColumns.tabletLandscape;
      /* This sets the width to be {itemColumns} columns + one gutter width; */
      return `calc(${(itemColumns / fluidGridColumns.tabletLandscape) * 100}%)`;
    }};

    /* This adds a bottom margin to all but the final two */
    &:nth-child(-n + 2) {
      margin-bottom: ${spacing(6)};
    }

    &:not(:nth-child(-n + 2)) {
      margin-bottom: 0;
    }
  `}

  ${mediaQuery.desktop`
     width: ${(props: ThemedStyledProps<GridItemProps, Theme>) => {
       const itemColumns = props.desktop ?? fluidGridColumns.desktop;
       return `calc(${(itemColumns / fluidGridColumns.desktop) * 100}%)`;
     }};
  `}
`;
